import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { parseErrorMessage } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Loader2, Send } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PrivacyPolicyNotifyStatus } from "./types";

export function PrivacyPolicyNotifyPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const statusQuery = useQuery<PrivacyPolicyNotifyStatus>({
    queryKey: ["/api/app/admin/privacy-policy-notification-status"],
    queryFn: async () => {
      const res = await fetch("/api/app/admin/privacy-policy-notification-status", {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to load Privacy Policy notification status");
      return res.json();
    },
    refetchInterval: 60_000,
  });

  const sendMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch("/api/app/admin/notify-privacy-policy-update", {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.message ?? "Failed to send Privacy Policy update emails");
      }
      return res.json() as Promise<{
        success: true;
        eligibleCount: number;
        sent: number;
        failed: number;
        failures: Array<{ email: string; reason: string }>;
        privacyPolicyVersion: string;
      }>;
    },
    onSuccess: (data) => {
      toast({
        title: "Privacy Policy emails processed",
        description: `Sent ${data.sent} of ${data.eligibleCount} (${data.failed} failed).`,
      });
      queryClient.invalidateQueries({
        queryKey: ["/api/app/admin/privacy-policy-notification-status"],
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Send failed",
        description: parseErrorMessage(err, "Unknown error"),
        variant: "destructive",
      });
    },
  });

  const status = statusQuery.data;
  const lastRun = status?.lastRun ?? null;
  const pending = status?.pendingCount ?? 0;
  const isLoading = statusQuery.isLoading;
  const isSending = sendMutation.isPending;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3
            className="text-lg font-semibold"
            data-testid="text-privacy-policy-notify-title"
          >
            Privacy Policy Update Notifier
          </h3>
          <p className="text-sm text-muted-foreground">
            Email active users about the latest published Privacy Policy. Idempotent — users
            already notified or who already acknowledged the current version are skipped.
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => sendMutation.mutate()}
          disabled={isSending || isLoading}
          data-testid="button-notify-privacy-policy"
        >
          {isSending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {pending > 0 ? `Send to ${pending} pending` : "Re-run now"}
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : !status ? (
        <Card className="p-6">
          <p className="text-sm text-muted-foreground" data-testid="text-privacy-policy-empty">
            Could not load Privacy Policy notification status.
          </p>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Current Version
              </div>
              <div
                className="mt-1 text-lg font-semibold"
                data-testid="text-privacy-policy-version"
              >
                {status.privacyPolicyVersion}
              </div>
              <div
                className="text-xs text-muted-foreground mt-1"
                data-testid="text-privacy-policy-label"
              >
                Updated {status.lastUpdatedLabel}
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Users Pending Notification
              </div>
              <div
                className="mt-1 text-2xl font-semibold"
                data-testid="text-privacy-policy-pending-count"
              >
                {pending}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                Active, approved users who haven't been emailed or acknowledged this version.
              </div>
            </Card>
            <Card className="p-4">
              <div className="text-xs uppercase text-muted-foreground tracking-wide">
                Last Run
              </div>
              {lastRun ? (
                <>
                  <div
                    className="mt-1 text-sm font-medium"
                    data-testid="text-privacy-policy-last-run-summary"
                  >
                    Sent {lastRun.sent} of {lastRun.eligibleCount}
                    {lastRun.failed > 0 ? ` (${lastRun.failed} failed)` : ""}
                  </div>
                  <div
                    className="text-xs text-muted-foreground mt-1"
                    data-testid="text-privacy-policy-last-run-meta"
                  >
                    {new Date(lastRun.finishedAt).toLocaleString()} · triggered by{" "}
                    {lastRun.triggeredBy}
                  </div>
                </>
              ) : (
                <div
                  className="mt-1 text-sm text-muted-foreground"
                  data-testid="text-privacy-policy-no-last-run"
                >
                  No run recorded for this version yet.
                </div>
              )}
            </Card>
          </div>

          <Card className="p-4">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">
              Change Summary (sent in email)
            </div>
            <p
              className="mt-1 text-sm whitespace-pre-wrap"
              data-testid="text-privacy-policy-change-summary"
            >
              {status.changeSummary}
            </p>
          </Card>

          {lastRun && lastRun.failures.length > 0 && (
            <Card className="p-4">
              <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-yellow-600" />
                Failed Sends ({lastRun.failures.length})
              </div>
              <div className="rounded border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lastRun.failures.map((f, idx) => (
                      <TableRow
                        key={`${f.email}-${idx}`}
                        data-testid={`row-privacy-policy-failure-${idx}`}
                      >
                        <TableCell
                          className="font-mono text-xs"
                          data-testid={`text-privacy-policy-failure-email-${idx}`}
                        >
                          {f.email}
                        </TableCell>
                        <TableCell
                          className="text-xs text-muted-foreground"
                          data-testid={`text-privacy-policy-failure-reason-${idx}`}
                        >
                          {f.reason}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
